import React, { useState } from "react";
import styles from "./originTableMobile.module.css";
import RailIconFO from "../../../icons/RailIconFO";
import TruckIconFO from "../../../icons/TruckIconFO";
import { IQuoteExtensionOld } from "../../../types/ICompAnalysisQMD";
import { icons } from "../../../svg";
import { calculateFobDifference } from "./multiLocationQuoteUtils";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import { useNitroStore } from "../../../store/Store";

const formatPrice = (price: number | null | undefined) => {
  if (price == null) return "$--";
  return `$${price.toFixed(0)}`;
};

interface OriginTableMobileProps {
  quotes: IQuoteExtensionOld[];
  destination?: any;
}

export const OriginTableMobile: React.FC<OriginTableMobileProps> = ({
  quotes,
  destination,
}) => {
  const [editingDlvdRowKey, setEditingDlvdRowKey] = useState<string | null>(
    null,
  );
  const [editingFobRowKey, setEditingFobRowKey] = useState<string | null>(null);
  const [rowDlvdValues, setRowDlvdValues] = useState<Record<string, string>>(
    {},
  );
  const [rowFobValues, setRowFobValues] = useState<Record<string, string>>({});
  const [originalValues, setOriginalValues] = useState<
    Record<string, { dlvd: string; fob: string }>
  >({});

  const [setOriginToTroubleshoot, setTroubleshootQuoteOpen] = useNitroCAStore(
    (state) => [state.setOriginToTroubleshoot, state.setTroubleshootQuoteOpen],
  );
  const [setSelectedLocation, setTempPinLocation] = useNitroStore((state) => [
    state.setSelectedLocation,
    state.setTempPinLocation,
  ]);

  const handleDlvdClick = (
    rowKey: string,
    currentDlvd: number | null,
    currentFob: number,
  ) => {
    setEditingDlvdRowKey(rowKey);
    const dlvdStr = currentDlvd != null ? currentDlvd.toFixed(0) : "";
    setRowDlvdValues((prev) => ({ ...prev, [rowKey]: dlvdStr }));

    if (!originalValues[rowKey]) {
      setOriginalValues((prev) => ({
        ...prev,
        [rowKey]: {
          dlvd: currentDlvd != null ? currentDlvd.toFixed(0) : "",
          fob: currentFob.toFixed(0),
        },
      }));
    }
  };

  const handleDlvdChange = (
    rowKey: string,
    value: string,
    freight: number | null,
  ) => {
    setRowDlvdValues((prev) => ({ ...prev, [rowKey]: value }));

    const dlvdValue = parseFloat(value);
    if (!Number.isNaN(dlvdValue) && freight != null) {
      const newFob = (dlvdValue - freight).toFixed(0);
      setRowFobValues((prev) => ({ ...prev, [rowKey]: newFob }));
    }
  };

  const handleDlvdBlur = () => {
    setEditingDlvdRowKey(null);
  };

  const handleDlvdKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      setEditingDlvdRowKey(null);
    }
  };

  const handleFobClick = (
    rowKey: string,
    currentFob: number,
    currentDlvd: number | null,
  ) => {
    setEditingFobRowKey(rowKey);
    setRowFobValues((prev) => ({ ...prev, [rowKey]: currentFob.toFixed(0) }));

    if (!originalValues[rowKey]) {
      setOriginalValues((prev) => ({
        ...prev,
        [rowKey]: {
          dlvd: currentDlvd != null ? currentDlvd.toFixed(0) : "",
          fob: currentFob.toFixed(0),
        },
      }));
    }
  };

  const handleFobChange = (
    rowKey: string,
    value: string,
    freight: number | null,
  ) => {
    setRowFobValues((prev) => ({ ...prev, [rowKey]: value }));

    const fobValue = parseFloat(value);
    if (!Number.isNaN(fobValue) && freight != null) {
      const newDlvd = (fobValue + freight).toFixed(0);
      setRowDlvdValues((prev) => ({ ...prev, [rowKey]: newDlvd }));
    }
  };

  const handleFobBlur = () => {
    setEditingFobRowKey(null);
  };

  const handleFobKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      setEditingFobRowKey(null);
    }
  };

  const handleResetRowValues = (rowKey: string) => {
    setRowDlvdValues((prev) => {
      const newValues = { ...prev };
      delete newValues[rowKey];
      return newValues;
    });
    setRowFobValues((prev) => {
      const newValues = { ...prev };
      delete newValues[rowKey];
      return newValues;
    });
    setOriginalValues((prev) => {
      const newValues = { ...prev };
      delete newValues[rowKey];
      return newValues;
    });
  };

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeader}>
        <img src={icons.MultiLocationsIcon} alt="MultiLocations Icon" />
        <span>All Origins</span>
      </div>

      {quotes.map((offer) => {
        const originName = offer.origin.name;
        const fobNetback = offer.maxPrice ?? 0;
        const freight = offer.freight?.totalFreight ?? null;
        const dlvdOffer =
          offer.maxPrice != null && freight != null
            ? fobNetback + freight
            : null;

        let netbackRange: string | null = null;
        if (offer.maxPrice != null) {
          if (offer.minPrice != null) {
            netbackRange = `($${fobNetback}-$${offer.minPrice})`;
          } else {
            netbackRange = `($${fobNetback}-$${fobNetback})`;
          }
        }

        const rowKey = `${offer.origin.id}-${offer.mode}-${
          offer.freight && "minCars" in offer.freight
            ? (offer.freight as any).minCars
            : "no-mincars"
        }`;

        const rowSpecificDlvd = rowDlvdValues[rowKey];
        const rowSpecificFob = rowFobValues[rowKey];
        const isEditingDlvd = editingDlvdRowKey === rowKey;
        const isEditingFob = editingFobRowKey === rowKey;

        let finalDlvdValue = dlvdOffer;
        if (rowSpecificDlvd) {
          finalDlvdValue = parseFloat(rowSpecificDlvd);
        }

        let finalFobValue = fobNetback;
        if (rowSpecificFob) {
          finalFobValue = parseFloat(rowSpecificFob);
        } else if (rowSpecificDlvd && freight !== null) {
          finalFobValue = parseFloat(rowSpecificDlvd) - freight;
        }

        const isDlvdModified =
          rowSpecificDlvd &&
          originalValues[rowKey] &&
          rowSpecificDlvd !== originalValues[rowKey].dlvd;
        const isFobModified =
          rowSpecificFob &&
          originalValues[rowKey] &&
          rowSpecificFob !== originalValues[rowKey].fob;

        const minPrice = offer.minPrice ?? fobNetback;
        const fobDifference = calculateFobDifference(
          finalFobValue,
          fobNetback,
          minPrice,
          !!isFobModified,
        );

        const fobStatusIcon = (() => {
          if (!isFobModified) {
            return null;
          }

          if (finalFobValue > fobNetback) {
            return (
              <img
                data-tooltip-id="CheckMarkCircleIcon"
                src={icons.CheckMarkCircleIcon}
                alt="Check Mark Circle Icon"
              />
            );
          }
          if (finalFobValue < minPrice) {
            return (
              <img
                data-tooltip-id="CrossCircleIcon"
                src={icons.CrossCircleIcon}
                alt="Cross Circle Icon"
              />
            );
          }
          if (finalFobValue < fobNetback && finalFobValue > minPrice) {
            return (
              <img
                data-tooltip-id="InformationIcon"
                src={icons.InformationIcon}
                alt="InformationIcon"
                className={styles.InformationIcon}
              />
            );
          }
          return null;
        })();

        return (
          <div key={rowKey} className={styles.originRow}>
            <div className={styles.rowHeaderWrapper}>
              <div className={styles.rowHeader}>
                {offer.mode.toUpperCase() === "RAIL" ? (
                  <RailIconFO color="#F97316" />
                ) : (
                  <TruckIconFO color="#60A5FA" />
                )}
                <span
                  className={`${styles.originName} ${
                    offer.competitor
                      ? styles.originNameCompetitor
                      : styles.originNameKoch
                  }`}
                >
                  {originName}
                </span>
              </div>
              {offer.mode.toUpperCase() === "RAIL" &&
                offer.freight != null &&
                "minCars" in offer.freight &&
                offer.freight.minCars != null && (
                  <span id="rail-car-info" className={styles.railCarInfo}>
                    {offer.freight.minCars === 1
                      ? "SINGLE"
                      : `${offer.freight.minCars}+ UT`}
                  </span>
                )}
            </div>

            <div className={styles.offerDetails}>
              <div className={`${styles.offerColumn} ${styles.dlvdColumn}`}>
                <span className={styles.detailLabel}>DLVD Offer</span>
                {isEditingDlvd ? (
                  <input
                    type="text"
                    value={rowSpecificDlvd || ""}
                    onChange={(e) =>
                      handleDlvdChange(rowKey, e.target.value, freight)
                    }
                    onBlur={handleDlvdBlur}
                    onKeyDown={handleDlvdKeyDown}
                    className={styles.editInput}
                  />
                ) : (
                  <div
                    className={`${styles.valueWithPadding} ${styles.detailValue} ${styles.detailValueClickable}`}
                    onClick={() =>
                      handleDlvdClick(rowKey, dlvdOffer, fobNetback)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleDlvdClick(rowKey, dlvdOffer, fobNetback);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {isDlvdModified && (
                      <img
                        data-tooltip-id="dot-icon"
                        src={icons.BlueDotIcon}
                        alt="Dot Icon"
                        className={styles.dotIcon}
                      />
                    )}
                    {formatPrice(finalDlvdValue)}
                    {!isDlvdModified && (
                      <img
                        src={icons.EditPencilBlueIcon}
                        alt="Edit Icon"
                        className={styles.editPencilIcon}
                      />
                    )}
                    {isDlvdModified && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResetRowValues(rowKey);
                        }}
                        className={styles.resetButton}
                      >
                        <img
                          src={icons.RefreshButtonIcon}
                          alt="Reset Icon"
                          className={`${styles.IconIncrease20} ${styles.shadowBackground}`}
                        />
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className={`${styles.offerColumn} ${styles.freightColumn}`}>
                <span className={styles.detailLabel}>Freight</span>
                {freight == null || freight === 0 ? (
                  <button
                    type="button"
                    className={styles.reviewFreightButton}
                    onClick={() => {
                      if (destination) {
                        const locationRecord = {
                          id: destination.id,
                          name: destination.name,
                          owner: destination.owner || "",
                          address: "",
                          city: destination.city || "",
                          state: "",
                          zip: "",
                          country: "",
                          price: null,
                          lat: destination.coordinate?.[0] || 0,
                          long: destination.coordinate?.[1] || 0,
                          products: [],
                          modes: [],
                          carriers: [],
                          pinType: "customer" as const,
                        };
                        setSelectedLocation(locationRecord);
                        setTempPinLocation({
                          lat: locationRecord.lat,
                          lng: locationRecord.long,
                        });
                      }

                      setOriginToTroubleshoot({
                        id: offer.origin.id,
                        name: offer.origin.name,
                        coordinate: offer.origin.coordinate,
                        isCity: offer.origin.isCity,
                      });
                      setTroubleshootQuoteOpen(true);
                    }}
                  >
                    Review Freight
                  </button>
                ) : (
                  <div
                    className={`${styles.valueWithPadding} ${styles.detailValueFreight}`}
                  >
                    {formatPrice(freight)}
                  </div>
                )}
              </div>
              <div className={styles.offerColumn}>
                {isEditingFob ? (
                  <>
                    <span className={styles.detailLabel}>FOB Netback</span>
                    <input
                      type="text"
                      value={rowSpecificFob || ""}
                      onChange={(e) =>
                        handleFobChange(rowKey, e.target.value, freight)
                      }
                      onBlur={handleFobBlur}
                      onKeyDown={handleFobKeyDown}
                      className={styles.editInput}
                    />
                  </>
                ) : (
                  <div
                    className={
                      fobDifference.backgroundColor !== "transparent"
                        ? styles.fobColumnWrapper
                        : ""
                    }
                  >
                    <span className={styles.detailLabel}>FOB Netback</span>
                    <div
                      className={`${styles.valueWithPadding} ${
                        styles.netbackValue
                      } ${styles.netbackValueClickable} ${
                        isFobModified ? styles.netbackValueModified : ""
                      }`}
                      onClick={() =>
                        handleFobClick(rowKey, fobNetback, dlvdOffer)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleFobClick(rowKey, fobNetback, dlvdOffer);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      style={{
                        backgroundColor: fobDifference.backgroundColor,
                        color: fobDifference.netbackTextColor || undefined,
                      }}
                    >
                      {isFobModified && (
                        <img
                          data-tooltip-id="dot-icon"
                          src={icons.BlueDotIcon}
                          alt="Dot Icon"
                          className={styles.dotIcon}
                        />
                      )}
                      {formatPrice(finalFobValue)}
                      {fobStatusIcon}
                      {!isFobModified && (
                        <img
                          src={icons.EditPencilBlueIcon}
                          alt="Edit Icon"
                          className={styles.editPencilIcon}
                        />
                      )}
                    </div>
                    {netbackRange && (
                      <span className={styles.detailSubtext}>
                        {netbackRange}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {fobDifference.hasBackgroundChange &&
                fobDifference.netbackDelta && (
                  <div
                    className={`${styles.offerColumn} ${styles.differenceColumn}`}
                  >
                    <span className={styles.detailLabel}>&nbsp;</span>
                    <div
                      className={`${styles.valueWithPadding} ${styles.detailValue}`}
                      style={
                        fobDifference.netbackTextColor
                          ? { color: fobDifference.netbackTextColor }
                          : undefined
                      }
                    >
                      {fobDifference.netbackDelta}
                    </div>
                  </div>
                )}

              {isFobModified && !isEditingFob && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResetRowValues(rowKey);
                  }}
                  className={styles.resetButton}
                >
                  <img
                    src={icons.RefreshButtonIcon}
                    alt="Reset Icon"
                    className={`${styles.IconIncrease20} ${styles.shadowBackground}`}
                  />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
